import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import { LoadingButton } from "@mui/lab";

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import FileOutlined from "../../assets/FileOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { useUserDisplayName } from "../../hooks/useUserDisplayName";
import {
  WaiverStatus,
  WaiverServiceRecord,
  WaiverServiceResponse,
  asMitigationFactorArray,
  getWaiverStatus,
  isWaiverDecided,
  isGeneralWaiverRecord,
  getWaiverMitigationDescriptions,
  getNeedsInfoComment,
  getWaiverDocuments,
  getLatestWaiverResponse,
  resetWaiverStatus,
  downloadWaiverDocument,
  fetchAndSyncWaivers,
} from "./utils";
import { Roles } from "../../utils/enums";
import { isUnderCcUwReview } from "../../utils/capitalMarketReview";
import WaiverActionForm from "./WaiverActionForm";
import WaiverInfoResponseForm from "./WaiverInfoResponseForm";

type ActionAudit = { by: string; on?: string } | null;

// Extracted from WaiverSummary's render so each block's own conditional
// branching (ternaries/&&) is scored against its own small function instead
// of stacking onto WaiverSummary's cognitive complexity. Each returns null
// internally when it has nothing to show, so call sites don't need to
// duplicate the same guard condition.

function WaiverDecidedByLine({
  isDecided,
  currentActionAudit,
  actionByName,
  statusLabelKey,
  t,
}: {
  isDecided: boolean;
  currentActionAudit: ActionAudit;
  actionByName?: string;
  statusLabelKey: string;
  t: (key: string) => string;
}) {
  if (!isDecided || !currentActionAudit?.by) return null;
  return (
    <div
      data-testid="waiver-decided-by"
      style={{
        fontSize: "12px",
        lineHeight: "20px",
        letterSpacing: "0.4px",
        color: "#383847",
      }}
    >
      {t(statusLabelKey)} {t("byLabel")}: {actionByName}{" "}
      &bull;{" "}
      {moment(currentActionAudit.on).format("MMM DD, YYYY, hh:mm A")}
    </div>
  );
}

function WaiverDocumentsList({
  docs,
  downloadingPath,
  onDownload,
}: {
  docs: { fileName: string; filePath: string }[];
  downloadingPath: string;
  onDownload: (filePath: string) => void;
}) {
  if (docs.length === 0) return null;
  return (
    <Grid
      item
      xs={12}
      style={{
        paddingTop: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {docs.map((doc, index) => (
        <div
          key={`${doc.fileName}-${index}`}
          data-testid="waiver-summary-document-item"
          style={{
            borderRadius: "4px",
            border: "1px solid #DBE2FF",
            background: "#F8F9FD",
            display: "flex",
            padding: "8px 10px",
            alignItems: "center",
            gap: "8px",
            alignSelf: "stretch",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
            <img
              src={FileOutlined}
              alt="file-icon"
              style={{ width: "16px", height: "16px", marginRight: "8px", flexShrink: 0 }}
            />
            <span
              title={doc.fileName}
              style={{
                color: "#383847",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "16px",
                letterSpacing: "0.064px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {doc.fileName}
            </span>
          </div>
          <IconButton
            size="small"
            aria-label="download"
            onClick={() => onDownload(doc.filePath)}
            disabled={downloadingPath === doc.filePath}
            data-testid="waiver-summary-download-button"
          >
            {downloadingPath === doc.filePath ? (
              <CircularProgress size={16} />
            ) : (
              <FileDownloadOutlinedIcon fontSize="small" style={{ color: "#383847" }}/>
            )}
          </IconButton>
        </div>
      ))}
    </Grid>
  );
}

function WaiverDecisionDetails({
  latestResponse,
  isDecided,
  actionByName,
  currentActionAudit,
  t,
}: {
  latestResponse: WaiverServiceResponse | undefined;
  isDecided: boolean;
  actionByName?: string;
  currentActionAudit: ActionAudit;
  t: (key: string) => string;
}) {
  if (!latestResponse || !isDecided) return null;
  return (
    <Grid
      item
      xs={12}
      style={{
        paddingTop: "16px",
        borderTop: "1px solid #DBE2FF",
        marginTop: "16px",
      }}
      data-testid="waiver-underwriter-action"
    >
      <div
        style={{
          borderRadius: "4px",
          border: "1px solid #DBE2FF",
          background: "#F8F9FD",
          padding: "12px",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            letterSpacing: "0.4px",
            fontWeight: 700,
            display: "block",
          }}
        >
          {t("compensatingFactor")}
        </span>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {asMitigationFactorArray(
            latestResponse?.compensatingFactors,
          ).map((factor) => (
            <Chip
              key={factor}
              size="small"
              label={factor}
              data-testid="compensating-factor-chip"
              style={{
                borderRadius: "40px",
                background: "#40B4B4",
                display: "flex",
                padding: "2px 8px",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                color: "#fff",
                height: "auto",
              }}
            />
          ))}
        </div>
        {latestResponse?.comments ? (
          <div style={{ marginTop: "12px" }} data-testid="waiver-decision-comment">
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 700,
                display: "block",
              }}
            >
              {t("waiverComments")}
            </span>
            <div style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>{latestResponse.comments}</div>
          </div>
        ) : null}
        {currentActionAudit?.by ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "12px",
                borderRadius: "12px",
                background: "#655DBB",
              }}
            >
              {getInitials(actionByName)}
            </Avatar>
            <span
              style={{
                color: "#383847",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                letterSpacing: "0.4px",
              }}
            >
              {actionByName} &bull;{" "}
              {moment(currentActionAudit.on).format(
                "MMM DD, YYYY, hh:mm A",
              )}
            </span>
          </div>
        ) : null}
      </div>
    </Grid>
  );
}

function WaiverActionsFooter({
  canActOnWaiver,
  isActionFormOpen,
  isDecided,
  isResetting,
  onReset,
  onOpenActionForm,
  resetError,
  t,
}: {
  canActOnWaiver: boolean;
  isActionFormOpen: boolean;
  isDecided: boolean;
  isResetting: boolean;
  onReset: () => void;
  onOpenActionForm: () => void;
  resetError: string;
  t: (key: string) => string;
}) {
  if (!canActOnWaiver || isActionFormOpen) return null;
  return (
    <Grid item xs={12} style={{ paddingTop: "16px" }}>
      {isDecided ? (
        <LoadingButton
          variant="outlined"
          size="small"
          loading={isResetting}
          onClick={onReset}
          data-testid="reset-waiver-button"
        >
          {t("resetWaiverText")}
        </LoadingButton>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<DoneOutlinedIcon fontSize="small" />}
          onClick={onOpenActionForm}
          data-testid="act-on-waiver-button"
        >
          {t("actOnWaiver")}
        </Button>
      )}
      {resetError ? (
        <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "8px" }}>
          {resetError}
        </div>
      ) : null}
    </Grid>
  );
}

const getInitials = (fullName: string | undefined) => {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// `cardColor` drives both the card's border and its header background — for
// decided waivers those are the same flat color; Pending keeps the original
// translucent-yellow border paired with a slightly different solid header
// tint, so it gets its own explicit `cardBorder`.
const STATUS_CONFIG: Record<
  string,
  {
    labelKey: string;
    color: string;
    cardBorder: string;
    cardBackground: string;
    icon?: typeof DoneOutlinedIcon;
  }
> = {
  [WaiverStatus.PENDING]: {
    labelKey: "pendingReview",
    color: "#F5A623",
    cardBorder: "rgba(252, 203, 29, 0.30)",
    cardBackground: "#FFF1DB",
  },
  [WaiverStatus.NEEDS_INFO]: {
    labelKey: "needAdditionalInfo",
    color: "#F5A623",
    cardBorder: "rgba(252, 203, 29, 0.30)",
    cardBackground: "#FFF1DB",
  },
  [WaiverStatus.APPROVED]: {
    labelKey: "testWaiverApproved",
    color: "#2d6c45",
    cardBorder: "#EDF7F1",
    cardBackground: "#EDF7F1",
    icon: DoneOutlinedIcon,
  },
  [WaiverStatus.REJECTED]: {
    labelKey: "testWaiverRejected",
    color: "#b30c00",
    cardBorder: "#fad0cd",
    cardBackground: "#fad0cd",
    icon: ClearOutlinedIcon,
  },
  [WaiverStatus.APPROVED_CONDITIONALLY]: {
    labelKey: "testWaiverApprovedConditionally",
    color: "#e38f0e",
    cardBorder: "#FFF1DB",
    cardBackground: "#FFF1DB",
    icon: ReportProblemOutlinedIcon,
  },
};

export default function WaiverSummary({
  waiver,
  canActOnWaiver = false,
}: {
  waiver: WaiverServiceRecord;
  canActOnWaiver?: boolean;
}) {
  const { t } = useTranslation();
  const { dynamicConfig, decodedTokenData, userPermissions } = useContext(RootContext) ?? {};
  const { loanDetails, setLoanDetails, setWaiverDecisionVersion } = useContext(LoansContext) ?? {};
  // A submitted loan is immutable — no waiver decision/reset action is
  // available regardless of the caller-passed canActOnWaiver permission.
  // Underwriter / credit committee may only act while the loan is actually
  // with them (CC/UW Review) — in LO Review or any other status the waiver is
  // read-only for them.
  const isUwOrCc = [
    Roles.underwriter,
    Roles.testUnderwriter,
    Roles.creditCommittee,
    Roles.testCreditCommittee,
  ].some((role) => userPermissions?.includes(role));
  const blockedByLoanStatus = isUwOrCc && !isUnderCcUwReview(loanDetails);
  const canActOnWaiverNow = canActOnWaiver && !loanDetails?.tcLoanId && !blockedByLoanStatus;
  const [downloadingPath, setDownloadingPath] = useState("");
  const [isActionFormOpen, setIsActionFormOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  const status = getWaiverStatus(waiver);
  const statusConfig = STATUS_CONFIG[status] ?? STATUS_CONFIG[WaiverStatus.PENDING];
  const isDecided = isWaiverDecided(status);
  const isNeedsInfo = status === WaiverStatus.NEEDS_INFO;
  const latestResponse = getLatestWaiverResponse(waiver);
  const currentActionAudit = latestResponse?.responseBy
    ? { by: latestResponse.responseBy, on: latestResponse.responseDate }
    : null;
  const docs = getWaiverDocuments(waiver);
  const hasDecisionDetails = Boolean(latestResponse) && isDecided;

  // createdBy/responseBy are Keycloak userIds on the raw waiver-service
  // record — resolve them to display names rather than showing the id.
  const createdByName = useUserDisplayName(waiver.createdBy);
  const actionByName = useUserDisplayName(currentActionAudit?.by);

  const downloadFile = async (filePath: string) => {
    setDownloadingPath(filePath);
    try {
      const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
      const url = await downloadWaiverDocument(waiver.waiverId ?? "", filePath, realmHeaders);
      // A programmatically-clicked <a> triggers the browser's native download
      // handling directly, unlike window.open(url) — which is unreliable for
      // presigned GCS URLs (typically served as a Content-Disposition:
      // attachment, so a new tab has nothing to render and just closes
      // itself) and gets popup-blocked outright if opened after this await.
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error("Something went wrong while downloading file. ERROR:", err);
    } finally {
      setDownloadingPath("");
    }
  };

  const handleResetWaiver = async () => {
    // Records the reset as a real "Re-Requested" response via the waiver
    // service — the same endpoint approve/reject/conditional-approve use —
    // so it persists across a refresh instead of being a local-only change.
    setIsResetting(true);
    setResetError("");
    try {
      await resetWaiverStatus(setLoanDetails, waiver, setWaiverDecisionVersion, decodedTokenData);
      // Re-fetches the whole list from the waiver service and recomputes the
      // waiverReviewStatus flag from it (see fetchAndSyncWaivers/
      // syncWaiverReviewStatusFlag) — same post-action pattern
      // WaiverActionForm.tsx already uses for decisions.
      // syncReviewStatusFlag=true: this is a real waiver action (reset), one
      // of the only cases that should re-patch waiverReviewStatus.
      await fetchAndSyncWaivers(waiver.sizerLoanId, setLoanDetails, setWaiverDecisionVersion, true);
    } catch (err) {
      setResetError(t("waiverActionFailed"));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Box
      data-testid="waiver-summary"
      sx={{
        marginTop: "24px",
        borderRadius: "4px 4px 0 0",
        borderTop: `1px solid ${statusConfig.cardBorder}`,
        borderRight: `1px solid ${statusConfig.cardBorder}`,
        borderLeft: `1px solid ${statusConfig.cardBorder}`,
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "10px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            borderTop: `1px solid ${statusConfig.cardBorder}`,
            borderRight: `1px solid ${statusConfig.cardBorder}`,
            borderLeft: `1px solid ${statusConfig.cardBorder}`,
            background: statusConfig.cardBackground,
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3
              style={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                margin: 0,
                padding: 0,
              }}
            >
              {t("testWaiverRequestedHeader")}
            </h3>
            <Chip
              size="small"
              label={t(statusConfig.labelKey)}
              icon={
                statusConfig.icon ? (
                  <statusConfig.icon fontSize="small" style={{ color: "#fff" }} />
                ) : undefined
              }
              style={{ background: statusConfig.color, color: "#fff" }}
            />
          </div>
          <WaiverDecidedByLine
            isDecided={isDecided}
            currentActionAudit={currentActionAudit}
            actionByName={actionByName}
            statusLabelKey={statusConfig.labelKey}
            t={t}
          />
        </Grid>
      </Grid>

      <Grid container style={{ padding: "16px" }}>
        <Grid item xs={12}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 700,
              display: "block",
            }}
          >
            {t("compensatingFactor")}
          </span>
          <div style={{ fontSize: "14px", fontWeight: 500 }}>
            {getWaiverMitigationDescriptions(waiver).join(", ")}
          </div>
        </Grid>

        {waiver.description ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 700,
                display: "block",
              }}
            >
              {isGeneralWaiverRecord(waiver)
                ? t("generalWaiverRequestDescription")
                : t("waiverComments")}
            </span>
            <div style={{ fontSize: "14px" }}>{waiver.description}</div>
          </Grid>
        ) : null}

        <WaiverDocumentsList
          docs={docs}
          downloadingPath={downloadingPath}
          onDownload={downloadFile}
        />

        <Grid
          item
          xs={12}
          style={{
            paddingTop: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: "12px",
              borderRadius: "12px",
              background: "#655DBB",
            }}
          >
            {getInitials(createdByName)}
          </Avatar>
          <span
            style={{
              color: "#383847",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              letterSpacing: "0.4px",
            }}
          >
            {createdByName} &bull;{" "}
            {moment(waiver.createdOn).format("MMM DD, YYYY, hh:mm A")}
          </span>
        </Grid>

        <WaiverDecisionDetails
          latestResponse={latestResponse}
          isDecided={isDecided}
          actionByName={actionByName}
          currentActionAudit={currentActionAudit}
          t={t}
        />

        <WaiverActionsFooter
          canActOnWaiver={canActOnWaiverNow}
          isActionFormOpen={isActionFormOpen}
          isDecided={isDecided}
          isResetting={isResetting}
          onReset={handleResetWaiver}
          onOpenActionForm={() => setIsActionFormOpen(true)}
          resetError={resetError}
          t={t}
        />
      </Grid>
      {hasDecisionDetails ? (
        <hr
          data-testid="waiver-decision-divider"
          style={{ border: "1px solid #DBE2FF", margin: "0px 16px", background: "#DBE2FF" }}
        />
      ) : null}
      {isNeedsInfo ? (
        <div style={{ padding: "0 16px" }} data-testid="waiver-needs-info-request">
          <span style={{ fontSize: "12px", fontWeight: 700, display: "block" }}>
            {t("needAdditionalInfo")}
          </span>
          <div style={{ fontSize: "14px", whiteSpace: "pre-wrap" }}>{getNeedsInfoComment(waiver)}</div>
        </div>
      ) : null}
      {isNeedsInfo && !canActOnWaiver && !loanDetails?.tcLoanId ? (
        <WaiverInfoResponseForm waiver={waiver} />
      ) : null}
      {canActOnWaiverNow && isActionFormOpen ? (
        <WaiverActionForm
          waiver={waiver}
          onClose={() => setIsActionFormOpen(false)}
        />
      ) : null}
    </Box>
  );
}
